import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import throttle from 'lodash/throttle';
import classNames from 'classnames';
import axios from 'axios';
import { FiMenu, FiSearch } from 'react-icons/fi';
import { IoArrowUp } from 'react-icons/io5';
import { FiPlus } from 'react-icons/fi';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
// import InfiniteScroll from 'react-infinite-scroll-component';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { addToLikeList } from '../../ducks/user.duck';

import { H3, H4, H5, NamedRedirect, Page, PrimaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';
import { withRouter } from 'react-router-dom';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import tshirtSvg from '../../assets/svg/tshirt_1.svg';
import { apiBaseUrl } from '../../util/api';
import { findLikedLists, loadData, loadMoreListings } from './SearchPage.duck';
import {
  getSearchPageWindowFlag,
  updateSearchPageWindowFlag,
  saveSearchPageScrollPos,
  getSearchPageScrollPos,
  restoreScrollPosition,
} from './helper';
import KKLoader from '../../components/KKLoader/KKLoader';
import SearchResultsSkeleton from '../../components/SearchResultsSkeleton/SearchResultsSkeleton';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export const SearchPageComponent = props => {
  const {
    intl,
    listings = [],
    location,
    onManageDisableScrolling,
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    searchParams = {},
    routeConfiguration,
    history,
    favroiteListingIds,
    config,
    params: currentPathParams = {},
    onAddList,
    isAuthenticated,
    currentPageResultIds,
    onFindLikedLists,
    onLoadMore,
  } = props;

  // State hooks
  const initialSearchParams = location?.search ? new URLSearchParams(location.search) : null;
  const isMobileFilterOpenInitial = initialSearchParams
    ? initialSearchParams.get('mobilesearchfilter') === 'open'
    : false;
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(isMobileFilterOpenInitial);
  const [currentQueryParams, setCurrentQueryParams] = useState(validUrlQueryParamsFromProps(props));
  const [brandSearchQuery, setBrandSearchQuery] = useState(' ');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Refs to track previous values
  const prevCurrentPageResultIdsRef = useRef();
  const prevMountedRef = useRef();

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  const onOpenMobileModal = useCallback(() => {
    setIsMobileModalOpen(true);
  }, []);

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  const onCloseMobileModal = useCallback(() => {
    setIsMobileModalOpen(false);
  }, []);

  const handleBrandSearchChange = useCallback(e => {
    setBrandSearchQuery(e.target.value);
  }, []);

  // Reset all filter query parameters
  const resetAll = useCallback(
    e => {
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};

      const urlQueryParams = validUrlQueryParamsFromProps(props);
      const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

      // Reset state
      setCurrentQueryParams({});

      // Reset routing params
      const queryParams = omit(urlQueryParams, filterQueryParamNames);

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );
      updateSearchPageWindowFlag({ preventLoading: false });

      history.push(
        createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
      );
    },
    [config, props, routeConfiguration, location, history]
  );

  const getHandleChangedValueFn = useCallback(
    useHistoryPush => {
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      const urlQueryParams = validUrlQueryParamsFromProps(props);

      return updatedURLParams => {
        setCurrentQueryParams(prevState => {
          const { address, bounds, keywords } = urlQueryParams;
          const mergedQueryParams = { ...urlQueryParams, ...prevState };
          const prevLevel2 = prevState?.pub_categoryLevel2;
          const nextLevel2 = updatedURLParams?.pub_categoryLevel2;

          if (nextLevel2 && prevLevel2 !== nextLevel2) {
            delete mergedQueryParams.pub_women_size;
            delete mergedQueryParams.pub_men_sizes;
            delete mergedQueryParams.pub_Womens_Shoes_Sizes;
            delete mergedQueryParams.pub_Mens_Shoes_Sizes;
            delete mergedQueryParams.pub_Women_Men_Accessories_size;
          }

          if (updatedURLParams.subcategory) {
            delete mergedQueryParams.category;
          }

          // Address and bounds are handled outside of MainPanel.
          // I.e. TopbarSearchForm && search by moving the map.
          // We should always trust urlQueryParams with those.
          // The same applies to keywords, if the main search type is keyword search.
          const sortMaybe = urlQueryParams[sortConfig.queryParamName]
            ? { [sortConfig.queryParamName]: urlQueryParams[sortConfig.queryParamName] }
            : {};

          const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
          const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

          const newQueryParams = omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              ...sortMaybe,
              address,
              bounds,
            },
            filterConfigs
          );

          // Execute callback after state update
          if (useHistoryPush) {
            setTimeout(() => {
              const searchParams = newQueryParams;
              const search = cleanSearchFromConflictingParams(
                searchParams,
                filterConfigs,
                sortConfig
              );

              const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
                routeConfiguration,
                location
              );
              updateSearchPageWindowFlag({ preventLoading: false });

              history.push(
                createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
              );
            }, 0);
          }

          return newQueryParams;
        });
      };
    },
    [config, props, currentPathParams, routeConfiguration, location, history]
  );

  const handleSortBy = useCallback(
    (urlParam, values) => {
      const urlQueryParams = validUrlQueryParamsFromProps(props);

      const queryParams = values
        ? { ...urlQueryParams, [urlParam]: values }
        : omit(urlQueryParams, urlParam);

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );
      updateSearchPageWindowFlag({ preventLoading: false });
      history.push(
        createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
      );
    },
    [props, routeConfiguration, location, history]
  );

  // Reset all filter query parameters
  const handleResetAll = useCallback(
    e => {
      resetAll(e);

      // blur event target if event is passed
      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
    },
    [resetAll]
  );

  const handleUpdateParams = useCallback(() => {
    setCurrentQueryParams(validUrlQueryParamsFromProps(config));
  }, [config]);

  const handleScroll = useCallback(
    throttle(() => {
      setShowScrollTop(window.scrollY > 300);
      if (typeof window !== 'undefined') {
        const searchParams = location?.search ? new URLSearchParams(location.search) : null;
        const isMobileFilterOpen = searchParams?.get('mobilesearchfilter') === 'open';
        if (!isMobileFilterOpen && window.scrollY > 0) {
          saveSearchPageScrollPos(location.pathname, location.search, {
            x: window.scrollX,
            y: window.scrollY,
          });
        }
      }
    }, 150),
    [location.pathname, location.search]
  );

  useEffect(() => {
    return () => {
      handleScroll.cancel();
      if (typeof window !== 'undefined' && window.scrollY > 0) {
        saveSearchPageScrollPos(location.pathname, location.search, {
          x: window.scrollX,
          y: window.scrollY,
        });
      }
    };
  }, [handleScroll, location.pathname, location.search]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadNextPage = useCallback(() => {
    updateSearchPageWindowFlag({ preventLoading: false });
    onLoadMore(config);
  }, [config, onLoadMore]);

  // Effect for restoring scroll position on mount or when returning to search page.
  // Only do this for back/forward (POP) navigation - filter changes push/replace the
  // URL too, and restoring a stale scroll position on those causes a small scroll jump.
  useEffect(() => {
    if (history.action !== 'POP') {
      return;
    }

    const searchParams = location?.search ? new URLSearchParams(location.search) : null;
    const isMobileFilterOpen = searchParams?.get('mobilesearchfilter') === 'open';
    if (isMobileFilterOpen) {
      return;
    }

    const scrollPos = getSearchPageScrollPos(location.pathname, location.search);
    if (scrollPos && (scrollPos.y > 0 || scrollPos.x > 0)) {
      restoreScrollPosition(scrollPos.y, scrollPos.x);
    }
  }, [history.action, location.pathname, location.search, listings.length]);

  // Effect for finding liked lists when currentPageResultIds changes
  useEffect(() => {
    if (prevCurrentPageResultIdsRef.current !== currentPageResultIds) {
      onFindLikedLists(currentPageResultIds);
    }
    prevCurrentPageResultIdsRef.current = currentPageResultIds;
  }, [currentPageResultIds, onFindLikedLists]);

  // Effect for syncing isMobileModalOpen with mobilesearchfilter query parameter
  useEffect(() => {
    const searchParams = location?.search ? new URLSearchParams(location.search) : null;
    const isOpen = searchParams ? searchParams.get('mobilesearchfilter') === 'open' : false;
    if (isOpen !== isMobileModalOpen) {
      setIsMobileModalOpen(isOpen);
    }
  }, [location.search, isMobileModalOpen]);

  // Effect for mounting and scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    setMounted(true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (typeof window !== 'undefined') {
        const searchParams = window.location?.search
          ? new URLSearchParams(window.location.search)
          : null;
        const isMobileFilterOpen = searchParams?.get('mobilesearchfilter') === 'open';
        if (!isMobileFilterOpen && window.scrollY > 0) {
          saveSearchPageScrollPos(location.pathname, location.search, {
            x: window.scrollX,
            y: window.scrollY,
          });
        }
      }
    };
  }, [handleScroll, location.pathname, location.search]);

  // If the search page variant is of type /s/:listingType, this defines the :listingType
  // path parameter used to filter the whole page.
  //
  // On a default search page (/s), this constant does not have a value, even when a
  // query parameter ?pub_listingType=[queryParamListingType] is used.
  const { listingType: listingTypePathParam } = currentPathParams;

  const { listingFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const defaultFiltersConfig = listingTypePathParam
    ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
    : defaultFiltersRaw;

  const marketplaceCurrency = config.currency;
  const categoryConfiguration = config.categoryConfiguration;
  const listingCategories = categoryConfiguration.categories;
  const listingFieldsConfig = useMemo(
    () =>
      pickListingFieldFilters({
        listingFields,
        locationSearch: location.search,
        categoryConfiguration,
        activeListingTypes,
        currentPathParams,
      }),
    [listingFields, location.search, categoryConfiguration, activeListingTypes, currentPathParams]
  );
  const filterConfigs = useMemo(
    () => ({
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    }),
    [
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    ]
  );

  // Page transition might initially use values from previous search
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = useMemo(
    () =>
      searchParamsPicker(
        location.search,
        searchParams,
        filterConfigs,
        sortConfig,
        isOriginInUse(config)
      ),
    [location.search, searchParams, filterConfigs, sortConfig, config]
  );
  const validQueryParams = urlQueryParams;

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
    ['isConsignment', 'categoryLevel', 'listingType'].includes(f.key)
  );
  const builtInFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(
        f => !['isConsignment', 'keywords', 'categoryLevel', 'listingType'].includes(f.key)
      )
    : defaultFiltersConfig.filter(
        f => !['isConsignment', 'categoryLevel', 'listingType'].includes(f.key)
      );
  const [customPrimaryFilters, customSecondaryFilters] = useMemo(
    () => groupListingFieldConfigs(listingFieldsConfig, activeListingTypes),
    [listingFieldsConfig, activeListingTypes]
  );
  const availableFilters = useMemo(
    () => [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ],
    [builtInPrimaryFilters, customPrimaryFilters, builtInFilters, customSecondaryFilters]
  );

  const filterInitialValues = useMemo(() => initialValues(props, currentQueryParams), [
    props,
    currentQueryParams,
  ]);

  // Selected aka active filters
  const selectedFilters = validQueryParams;
  const isValidDatesFilter =
    searchParamsInURL.dates == null ||
    (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const totalItems =
    // searchParamsAreInSync && hasPaginationInfo
    hasPaginationInfo
      ? pagination.totalItems
      : pagination?.paginationUnsupported
      ? listings.length
      : 0;

  const listingsAreLoaded =
    !searchInProgress &&
    // searchParamsAreInSync &&
    !!(hasPaginationInfo || pagination?.paginationUnsupported);

  const handleSellNowClick = () => {
    if (isAuthenticated) {
      history.push(createResourceLocatorString('NewListingPage', routeConfiguration, {}, {}));
    } else {
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}));
    }
  };

  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    filterConfigs
  );
  // console.log(1212, selectedFilters, keysOfSelectedFilters);
  const isMetaTrendingSortActive =
    validQueryParams[sortConfig.queryParamName] === 'meta_trendingScore';

  const sortBy = mode => {
    return sortConfig.active && !isMetaTrendingSortActive ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={handleSortBy}
        showAsPopup
        mode={mode}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;
  };
  const noResultsInfo = (
    <NoSearchResultsMaybe
      listingsAreLoaded={listingsAreLoaded}
      totalItems={totalItems}
      location={location}
      resetAll={resetAll}
    />
  );

  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    intl,
    routeConfiguration,
    config,
    location,
    currentPathParams
  );

  // Set topbar class based on if a modal is open in
  // a child component
  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;
  // N.B. openMobileMap button is sticky.
  // For some reason, stickyness doesn't work on Safari, if the element is <button>
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
    >
      <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
      <div className={css.layoutWrapperContainer}>
        <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
          {keysOfSelectedFilters.length ? (
            <button className={css.resetAllButton} onClick={e => handleResetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          ) : null}
          <div className={css.filterColumnContent}>
            {availableFilters.map(filterConfig => {
              const isBrandFilter = filterConfig?.label === 'BRAND';
              const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                filterConfig.key
              }`;
              return (
                <React.Fragment key={key}>
                  {isBrandFilter && (
                    <div className={css.brandSearchWrapper}>
                      <input
                        type="text"
                        className={css.brandSearchInput}
                        placeholder="Search Brands"
                        value={brandSearchQuery}
                        onChange={handleBrandSearchChange}
                      />
                    </div>
                  )}
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersDesktop"
                    className={css.filter}
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    // initialValues={initialValues(props, currentQueryParams)}
                    initialValues={filterInitialValues}
                    getHandleChangedValueFn={getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                    location={location}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </aside>

        <div className={css.layoutWrapperMain} role="main">
          <div className={css.searchResultContainer}>
            <SearchFiltersMobile
              className={css.searchFiltersMobileList}
              urlQueryParams={validQueryParams}
              sortByComponent={sortBy('mobile')}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={onManageDisableScrolling}
              onOpenModal={onOpenMobileModal}
              onCloseModal={onCloseMobileModal}
              resetAll={resetAll}
              // resetAll={this.handleResetAll}
              selectedFiltersCount={selectedFiltersCountForMobile}
              isMapVariant={false}
              noResultsInfo={noResultsInfo}
            >
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;

                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersMobile"
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(props, currentQueryParams)}
                    getHandleChangedValueFn={getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    location={location}
                    showAsPopup={false}
                  />
                );
              })}
            </SearchFiltersMobile>
            <MainPanelHeader
              className={css.mainPanel}
              sortByComponent={sortBy('desktop')}
              isSortByActive={sortConfig.active && !isMetaTrendingSortActive}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
              noResultsInfo={noResultsInfo}
            />
            {/* {totalItems > 0 && ( */}
            <div
              className={classNames(css.listingsForGridVariant, {
                [css.newSearchInProgress]:
                  !searchInProgress && !(listingsAreLoaded || searchListingsError),
                [css.noListing]: listingsAreLoaded && !searchInProgress && totalItems === 0,
              })}
            >
              {searchListingsError ? (
                <H3 className={css.error}>
                  <FormattedMessage id="SearchPage.searchError" />
                </H3>
              ) : null}
              {!isValidDatesFilter ? (
                <H5>
                  <FormattedMessage id="SearchPage.invalidDatesFilter" />
                </H5>
              ) : null}
              {searchInProgress ? (
                <div className={css.loaderContainer}>
                  <SearchResultsSkeleton count={pagination?.perPage || 12} />
                </div>
              ) : totalItems !== 0 ? (
                <InfiniteScroll
                  className={css.infiniteScroll}
                  dataLength={listings.length}
                  next={loadNextPage}
                  hasMore={
                    !!pagination &&
                    !pagination.paginationUnsupported &&
                    pagination.page < pagination.totalPages
                  }
                  loader={
                    <H5 className={css.loadingMore}>
                      <FormattedMessage
                        id="SearchPage.loadingMore"
                        defaultMessage="Loading more…"
                      />
                    </H5>
                  }
                  endMessage={
                    <H5 className={css.noMore}>
                      <FormattedMessage
                        id="SearchPage.endMessage"
                        defaultMessage="You’ve reached the end"
                      />
                    </H5>
                  }
                  style={{ overflow: 'visible' }}
                >
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    // pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    listingTypeParam={listingTypePathParam}
                    history={history}
                    favroiteListingIds={favroiteListingIds}
                    onAddList={onAddList}
                  />
                </InfiniteScroll>
              ) : null}
              {showScrollTop && (
                <div className={css.scrollToTopWrapper}>
                  <button onClick={scrollToTop} className={css.scrollToTopButton}>
                    <IoArrowUp />
                  </button>
                </div>
              )}{' '}
              <div className={css.sellNowWrapper}>
                <button onClick={handleSellNowClick} className={css.sellNowBtn}>
                  <FiPlus className={css.sellNowArrow} />
                  <FormattedMessage id="TopbarDesktop.createNewListing" />
                </button>
              </div>
            </div>
            {/* )} */}
            {!searchInProgress && listingsAreLoaded && totalItems === 0 && (
              <div className={css.noListingFallbackContainer}>
                <div className={css.fallbackHeading}>
                  <img src={tshirtSvg} alt="" loading="lazy" />
                  <H4 as="h1">
                    {intl
                      .formatMessage({ id: 'SearchPage.noListingFallbackTextHeading' })
                      .toUpperCase()}
                  </H4>
                  <p>{intl.formatMessage({ id: 'SearchPage.noListingFallbackText' })}</p>
                </div>
                <div className={css.fallbackButtonContainer}>
                  <PrimaryButton
                    inProgress={searchInProgress}
                    className={css.primBtn}
                    onClick={() => resetAll()}
                    // disabled={Object.keys(selectedFilters).length === 0}
                  >
                    {intl
                      .formatMessage({ id: 'SearchPage.noListingFallbackClearFiltersBtn' })
                      .toUpperCase()}
                  </PrimaryButton>
                  <PrimaryButton className={css.secdBtn} onClick={handleSellNowClick}>
                    {intl
                      .formatMessage({ id: 'SearchPage.noListingFallbackStartSellingBtn' })
                      .toUpperCase()}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <FooterContainer /> */}
    </Page>
  );
};

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.auth;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    favroiteListingIds,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    isAuthenticated,
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPageResultIds,
    favroiteListingIds,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFindLikedLists: listingIds => dispatch(findLikedLists(listingIds)),
  onAddList: (listingId, authorId) => dispatch(addToLikeList(listingId, authorId)),
  onLoadMore: config => dispatch(loadMoreListings(config)),
  onLoadData: (config, pathParams, searchParams) =>
    dispatch(loadData(pathParams, searchParams, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(EnhancedSearchPage);

export default SearchPage;
